import type { CatalogItem, VisitServiceRequest } from "@vaulted/shared";
import { formatCents, visitConflicts } from "@vaulted/shared";
import { Checkbox, Field, SelectField, cx } from "./ui";

/** Can this add-on ride on this service (its category, or the service itself)? */
export const attaches = (addOn: CatalogItem, service: CatalogItem) => (!!service.categoryId && addOn.attachToCategoryIds.includes(service.categoryId)) || addOn.attachToServiceIds.includes(service.id);

/**
 * A visit's services: any number of them, each with the add-ons that ride on it. Add-ons are picked
 * under their own service (Toradol in the drip), never on their own.
 */
export function ServicesPicker({
  items,
  services,
  value,
  onChange,
  price,
  keep = [],
  reason,
  onReason,
  asList,
  offer,
}: {
  items: CatalogItem[];
  /** The services that can be added here (offered in this context, active). */
  services: CatalogItem[];
  value: VisitServiceRequest[];
  onChange: (v: VisitServiceRequest[]) => void;
  /** An item's price here (clinic, mobile, or the event's). */
  price: (i: CatalogItem) => number;
  /** Add-ons already on the visit that stay listed even if archived. */
  keep?: string[];
  /** Why it's booked against the visit rules (two drips, or a medication twice), when it is. */
  reason?: string;
  onReason?: (r: string) => void;
  /** Nothing picked yet: show the services as a list to tap (booking), not a dropdown. */
  asList?: boolean;
  /** One tap to fill in, shown above the list (their last visit). */
  offer?: { label: string; value: VisitServiceRequest[] } | null;
}) {
  const set = (i: number, patch: Partial<VisitServiceRequest>) => onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addable = services.filter((s) => !value.some((v) => v.serviceId === s.id));
  const lines = value.flatMap((sv) => {
    const service = items.find((x) => x.id === sv.serviceId);
    return service ? [{ service, addOns: items.filter((x) => sv.addOnIds.includes(x.id)) }] : [];
  });
  const broken = visitConflicts(lines);
  return (
    <div className="flex flex-col gap-2">
      {value.map((sv, i) => {
        const service = items.find((x) => x.id === sv.serviceId);
        if (!service) return null;
        const addOns = items.filter((x) => x.type === "add_on" && (x.status === "active" || sv.addOnIds.includes(x.id) || keep.includes(x.id)) && attaches(x, service));
        return (
          <div key={sv.serviceId} className="rounded-[var(--radius-control)] border border-line bg-white">
            <div className="flex items-center justify-between gap-3 px-3 py-2">
              <div>
                <span className="font-semibold">{service.name}</span> <span className="text-sm text-ink-soft">{formatCents(price(service))}</span>
              </div>
              {(value.length > 1 || asList) && (
                <button type="button" className="text-sm font-semibold text-muted hover:text-danger" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label={`Remove ${service.name}`}>
                  Remove
                </button>
              )}
            </div>
            {addOns.length > 0 && (
              <details className="border-t border-mist px-3 py-2" open={sv.addOnIds.length > 0}>
                <summary className="cursor-pointer text-sm font-semibold text-brand">
                  Add-ons for {service.name} {sv.addOnIds.length ? `(${sv.addOnIds.length})` : ""}
                </summary>
                <div className="mt-1 grid sm:grid-cols-2">
                  {addOns.map((x) => (
                    <Checkbox
                      key={x.id}
                      label={x.name}
                      description={service.includedAddOnIds.includes(x.id) ? "Included" : formatCents(price(x))}
                      checked={sv.addOnIds.includes(x.id)}
                      onChange={(on) => set(i, { addOnIds: on ? [...sv.addOnIds, x.id] : sv.addOnIds.filter((y) => y !== x.id) })}
                    />
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}
      {asList && value.length === 0 ? (
        <div className="flex flex-col gap-1.5">
          {offer && (
            <button type="button" onClick={() => onChange(offer.value)} className="rounded-[var(--radius-control)] border border-brand bg-brand/5 px-3 py-2 text-left hover:bg-brand/10">
              <span className="block text-xs font-semibold text-brand">{offer.label}</span>
              <span className="font-semibold">
                {offer.value
                  .map((sv) => [items.find((x) => x.id === sv.serviceId)?.name, ...sv.addOnIds.map((id) => items.find((x) => x.id === id)?.name)].filter(Boolean).join(" + "))
                  .join(", ")}
              </span>
            </button>
          )}
          <p className="text-sm text-muted">Pick a service</p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {addable.map((s) => (
              <button key={s.id} type="button" onClick={() => onChange([{ serviceId: s.id, addOnIds: [] }])} className="flex items-center justify-between gap-2 rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-left hover:border-brand">
                <span className="font-semibold">{s.name}</span>
                <span className="text-sm text-ink-soft tabular-nums">{formatCents(price(s))}</span>
              </button>
            ))}
          </div>
        </div>
      ) : addable.length > 0 && (
        <SelectField
          label={value.length ? "Add a service" : "Service"}
          className={cx(value.length > 0 && "max-w-sm")}
          value=""
          onChange={(e) => e.target.value && onChange([...value, { serviceId: e.target.value, addOnIds: [] }])}
        >
          <option value="">{value.length ? "Add another service…" : "Pick a service…"}</option>
          {addable.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} · {formatCents(price(s))}
            </option>
          ))}
        </SelectField>
      )}
      {broken.length > 0 && (
        <div role="status" className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-wait-wash px-3.5 py-2.5 text-sm text-wait">
          {broken.map((b) => (
            <p key={b}>{b}</p>
          ))}
          {onReason && <Field label="Reason to book it anyway" required maxLength={300} value={reason ?? ""} onChange={(e) => onReason(e.target.value)} />}
        </div>
      )}
    </div>
  );
}
